import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { X, Edit2, Save, Loader, ChevronDown, ChevronUp, Lock } from "lucide-react";
import toast from "react-hot-toast";
import { editOrder } from "../../redux/reduxSlices/ordersSlice";

const EditOrderPopup = ({ orderId, onClose, onSave }) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});

    const {
        loading,
        orderByIdData,
    } = useSelector((state) => state.orders);

    // URL validation function
    const validateURL = (value) => {
        if (!value || value.trim() === '') return true; // Allow empty

        // Accept URLs with or without protocol, with or without www
        const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

        return urlPattern.test(value) || 'Please enter a valid URL';
    };

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {},
        mode: 'onChange'
    });

    // Categorize fields based on key patterns
    const categorizeField = (key) => {
        const keyLower = key.toLowerCase();

        if (keyLower.includes('mobile') || keyLower.includes('email') ||
            keyLower.includes('contact') || keyLower.includes('phone')) {
            return "contact";
        } else if (keyLower.includes('address') || keyLower.includes('city') ||
            keyLower.includes('website') || keyLower.includes('gst')) {
            return "company";
        } else if (keyLower.includes('brand') || keyLower.includes('instagram') ||
            keyLower.includes('vision') || keyLower.includes('product')) {
            return "brand";
        } else if (keyLower.includes('jingle') || keyLower.includes('language') ||
            keyLower.includes('tone') || keyLower.includes('voice') ||
            keyLower.includes('audience')) {
            return "requirements";
        } else if (keyLower.includes('tagline') || keyLower.includes('slogan') ||
            keyLower.includes('keyword') || keyLower.includes('phrase')) {
            return "content";
        } else if (keyLower.includes('timeline') || keyLower.includes('deadline')) {
            return "timeline";
        } else if (keyLower.includes('restriction') || keyLower.includes('sensitivity')) {
            return "restrictions";
        } else if (keyLower.includes('budget') || keyLower.includes('constraint') ||
            keyLower.includes('requirement')) {
            return "budget";
        } else if (keyLower.includes('order_id') || keyLower.includes('orderid') ||
            keyLower.includes('order') && keyLower.includes('id')) {
            return "basic"; // Special category for order_id
        } else {
            return "other";
        }
    };

    // Fields that should be disabled (non-editable)
    const disabledFields = [
        'order_id', 'orderid', 'orderId',
        'email', 'email_address', 'emailAddress',
        'mobile', 'mobile_number', 'mobileNumber', 'phone', 'phone_number',
        // Add any other field names that should be disabled
    ];

    // Check if a field should be disabled
    const shouldDisableField = (fieldName) => {
        const fieldNameLower = fieldName.toLowerCase();
        return disabledFields.some(disabledField =>
            fieldNameLower.includes(disabledField.toLowerCase())
        );
    };

    // Fields to exclude from the form completely
    const excludedFields = [
        '_id', '__v', 'createdAt', 'updatedAt',
        'created_at', 'updated_at', 'created_by', 'updated_by',
        'lyrics', 'song_uploads_with_watermark', 'song_uploads_without_watermark',
    ];

    // Helper function to format URL
    const formatURL = (url) => {
        if (!url || url.trim() === '') return '';

        const trimmedUrl = url.trim();

        // If URL already has protocol, return as is
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
            return trimmedUrl;
        }

        // Add https:// by default
        return `https://${trimmedUrl}`;
    };

    // Group fields by category
    const groupedFields = useMemo(() => {
        if (!orderByIdData) return {};

        const groups = {
            basic: { title: "Basic Information", fields: [] },
            contact: { title: "Contact Information", fields: [] },
            company: { title: "Company Information", fields: [] },
            brand: { title: "Brand Information", fields: [] },
            requirements: { title: "Jingle Requirements", fields: [] },
            content: { title: "Content Details", fields: [] },
            timeline: { title: "Timeline", fields: [] },
            restrictions: { title: "Restrictions", fields: [] },
            budget: { title: "Budget & Requirements", fields: [] },
            other: { title: "Additional Information", fields: [] }
        };

        Object.keys(orderByIdData).forEach(key => {
            if (excludedFields.includes(key)) {
                return;
            }

            const category = categorizeField(key);
            const value = orderByIdData[key];

            let type = "text";
            const keyLower = key.toLowerCase();

            if (keyLower.includes('email')) {
                type = "email";
            } else if (keyLower.includes('website') || keyLower.includes('url') || keyLower.includes('link')) {
                type = "url";
            } else if (keyLower.includes('description') || keyLower.includes('details') ||
                keyLower.includes('about') || keyLower.includes('message') ||
                keyLower.includes('requirement') || keyLower.includes('specify') ||
                (typeof value === 'string' && value.length > 100)) {
                type = "textarea";
            } else if (keyLower.includes('date')) {
                type = "date";
            } else if (keyLower.includes('number') || keyLower.includes('phone') || keyLower.includes('mobile')) {
                type = "tel";
            }

            const label = key;
            const isDisabled = shouldDisableField(key);

            groups[category].fields.push({
                key,
                label,
                type,
                value: value || "",
                isLongText: type === "textarea" || (typeof value === 'string' && value.length > 50),
                isURL: type === "url",
                disabled: isDisabled,
                disabledReason: isDisabled ? "This field cannot be edited" : null
            });
        });

        Object.keys(groups).forEach(groupKey => {
            if (groups[groupKey].fields.length === 0) {
                delete groups[groupKey];
            }
        });

        return groups;
    }, [orderByIdData]);

    // Initialize form with order data
    useEffect(() => {
        if (orderByIdData) {
            const formData = {};

            Object.keys(orderByIdData).forEach(key => {
                if (excludedFields.includes(key)) {
                    return;
                }

                const value = orderByIdData[key];
                formData[key] = value !== null && value !== undefined ? value : "";
            });

            reset(formData);

            const initialExpandedState = {};
            Object.keys(groupedFields).forEach(group => {
                initialExpandedState[group] = true;
            });
            setExpandedGroups(initialExpandedState);
        }
    }, [orderByIdData, reset, groupedFields]);

    const toggleGroup = (groupName) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupName]: !prev[groupName]
        }));
    };

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(() => onClose(), 300);
    };

    const onSubmit = async (data) => {
        // console.log("dddddd",data)
        // return
        setSubmitting(true);
        try {
            // Format URLs before submitting
            const formattedData = { ...data };

            Object.keys(formattedData).forEach(key => {
                const keyLower = key.toLowerCase();
                if ((keyLower.includes('website') || keyLower.includes('url') || keyLower.includes('link'))
                    && formattedData[key]) {
                    formattedData[key] = formatURL(formattedData[key]);
                }
            });

            console.log("Form data to submit:", formattedData);

            // Keep disabled field values from original data
            disabledFields.forEach(field => {
                if (orderByIdData[field] !== undefined) {
                    formattedData[field] = orderByIdData[field];
                }
            });

            const payload = {
                order_id: orderId,
                ...formattedData
            };

            const result = await dispatch(editOrder(payload)).unwrap();

            if (result.success) {
                toast.success("Order updated successfully!");
                if (onSave) {
                    onSave();
                }
                handleClose();
            } else {
                toast.error(result.message || "Failed to update order");
            }
        } catch (error) {
            toast.error(error.message || "Something went wrong");
            console.error("Update error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    // Render form fields based on grouped data
    const renderFormFields = () => {
        if (Object.keys(groupedFields).length === 0) {
            return (
                <div className="text-center py-8 text-gray-500">
                    No form fields available
                </div>
            );
        }

        return Object.entries(groupedFields).map(([groupKey, group]) => (
            <div key={groupKey} className="space-y-3 border border-gray-200 rounded-lg p-4">
                <button
                    type="button"
                    onClick={() => toggleGroup(groupKey)}
                    className="flex items-center justify-between w-full text-left"
                >
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">
                            {group.title} ({group.fields.length} fields)
                        </h3>
                        {group.fields.some(f => f.disabled) && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                Some fields are locked
                            </span>
                        )}
                    </div>
                    {expandedGroups[groupKey] ? (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                </button>

                {expandedGroups[groupKey] && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                        {group.fields.map((field) => (
                            <div
                                key={field.key}
                                className={field.isLongText ? "md:col-span-2" : ""}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <label className={`block text-xs font-medium ${field.disabled ? 'text-gray-500' : 'text-gray-700'}`}>
                                        {field.label}
                                    </label>
                                    {field.disabled && (
                                        <span className="flex items-center gap-1 text-xs text-gray-500">
                                            <Lock className="w-3 h-3" />
                                            Locked
                                        </span>
                                    )}
                                </div>

                                {field.type === "textarea" ? (
                                    <textarea
                                        {...register(field.key)}
                                        disabled={field.disabled}
                                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-none min-h-[80px] ${field.disabled
                                                ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                                                : 'border-gray-300 focus:ring-orange-500 focus:border-transparent'
                                            }`}
                                        placeholder={field.disabled ? "This field cannot be edited" : `Enter ${field.label}`}
                                        rows={3}
                                    />
                                ) : field.type === "url" ? (
                                    <>
                                        <input
                                            type="text"
                                            {...register(field.key, {
                                                validate: validateURL
                                            })}
                                            disabled={field.disabled}
                                            className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:border-transparent ${field.disabled
                                                    ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                                                    : 'border-gray-300 focus:ring-orange-500 focus:border-transparent'
                                                }`}
                                            placeholder={field.disabled ? "This field cannot be edited" : "example.com or https://example.com"}
                                        />
                                        {errors[field.key] && !field.disabled && (
                                            <p className="mt-1 text-xs text-red-600">{errors[field.key].message}</p>
                                        )}
                                        {!field.disabled && (
                                            <p className="mt-1 text-xs text-gray-500">
                                                Enter URL with or without https:// (e.g., example.com, https://example.com)
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <input
                                        type={field.type}
                                        {...register(field.key)}
                                        disabled={field.disabled}
                                        className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:border-transparent ${field.disabled
                                                ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                                                : 'border-gray-300 focus:ring-orange-500 focus:border-transparent'
                                            }`}
                                        placeholder={field.disabled ? "This field cannot be edited" : `Enter ${field.label}`}
                                    />
                                )}

                                {field.disabled && field.disabledReason && (
                                    <p className="mt-1 text-xs text-gray-500 italic">
                                        {field.disabledReason}
                                    </p>
                                )}

                                {field.type !== "url" && errors[field.key] && !field.disabled && (
                                    <p className="mt-1 text-xs text-red-600">{errors[field.key].message}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
            <div
                className="bg-white rounded-lg border border-gray-200 w-full max-w-6xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
                    <div className="flex items-center gap-2">
                        <Edit2 className="w-5 h-5 text-orange-600" />
                        <div>
                            <h2 className="text-base font-bold text-gray-900">
                                Edit Order: {orderId}
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Some fields are locked and cannot be edited
                            </p>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {Object.keys(groupedFields).length} groups, {
                                Object.values(groupedFields).reduce((total, group) => total + group.fields.length, 0)
                            } fields
                        </span>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 p-1"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="p-4">
                    <div className="space-y-4">
                        {loading ? (
                            <div className="text-center py-8">
                                <Loader className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
                                <p className="text-gray-600 text-sm mt-2">Loading order details...</p>
                            </div>
                        ) : (
                            renderFormFields()
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 mt-6 border-t border-gray-200 pt-4 sticky bottom-0 bg-white">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 flex items-center gap-1.5"
                            disabled={submitting || loading}
                        >
                            {submitting ? (
                                <>
                                    <Loader className="w-3.5 h-3.5 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-3.5 h-3.5" />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditOrderPopup;